import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Confirm, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, Switch } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { action, subscribe } from '../lib/api.ts';
import { day as dayText, todayIso } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { Kpi, Table } from './Reports.tsx';

/**
 * eLearning: my courses (lessons to read or watch, quizzes, a certificate at the end) and, for
 * managers, writing courses, assigning them and following everyone's results.
 */

type Kind = 'text' | 'video' | 'document' | 'quiz';
const KINDS: Kind[] = ['text', 'video', 'document', 'quiz'];
const KIND_ICON: Record<Kind, IconName> = { text: 'text', video: 'video', document: 'file', quiz: 'checkSquare' };
const TONES = ['blue', 'green', 'orange', 'purple', 'red', 'teal'] as const;

interface MyCourse {
  id: string;
  title: string;
  summary: string | null;
  category: string | null;
  tone: string;
  certificate: boolean;
  enrolment: string | null;
  state: 'new' | 'assigned' | 'started' | 'done';
  due: string | null;
  done_at: string | null;
  score: number | null;
  certificate_no: number | null;
  lessons: number;
  minutes: number;
  lessons_done: number;
  late: boolean;
}
interface Outline {
  course: { id: string; title: string; summary: string | null; category: string | null; tone: string; pass_mark: number; certificate: boolean };
  enrolment: { id: string; state: string; due: string | null; done_at: string | null; score: number | null; certificate_no: number | null } | null;
  lessons: { id: string; title: string; kind: Kind; minutes: number; questions: number; done: boolean; best: number | null; attempts: number }[];
}
interface LessonView {
  id: string;
  course: string;
  title: string;
  kind: Kind;
  body: string | null;
  url: string | null;
  minutes: number;
  pass_mark: number;
  done: boolean;
  best: number | null;
  attempts: number;
  questions: { id: string; text: string; options: string[]; multiple: boolean }[];
}
interface QuizResult {
  score: number;
  passed: boolean;
  pass_mark: number;
  course_done: boolean;
  certificate_no: number | null;
  questions: { id: string; right: boolean; correct: number[]; explain: string | null }[];
}

const minutesText = (m: number) => (m >= 60 ? t('learn.hours_min', { h: Math.floor(m / 60), m: m % 60 }) : tn('learn.minutes', m));
const pct = (done: number, all: number) => (all ? Math.round((done / all) * 100) : 0);

function Progress({ value, label }: { value: number; label: string }) {
  return (
    <span className="lr-progress" role="progressbar" aria-valuenow={value} aria-valuemin={0} aria-valuemax={100} aria-label={label}>
      <span style={{ width: `${value}%` }} />
    </span>
  );
}

/** Text lessons: paragraphs separated by an empty line, lines kept as written. */
function Body({ text }: { text: string | null }) {
  if (!text) return null;
  return (
    <div className="lr-body">
      {text.split(/\n\s*\n/).map((p, i) => (
        <p key={i}>{p}</p>
      ))}
    </div>
  );
}

// ---- my learning --------------------------------------------------------------------------

export function Learning() {
  const { can } = useSession();
  const { data, reload } = useAction<{ today: string; courses: MyCourse[]; counts: { todo: number; late: number; done: number } }>('elearning.my');
  const [cert, setCert] = useState<string | null>(null);
  useEffect(() => subscribe(['elearning'], () => void reload()), [reload]);
  return (
    <Shell>
      <div className="page reports lr-page">
        <div className="page-head">
          <div>
            <h1>{t('learn.title')}</h1>
            <p className="muted">{t('learn.subtitle')}</p>
          </div>
          {can('elearning.manage') ? (
            <Link className="btn btn-secondary btn-md" to="/learning/manage">
              <Icon name="edit" size={20} />
              <span className="btn-label">{t('learn.manage')}</span>
            </Link>
          ) : null}
        </div>
        {!data ? (
          <Skeleton h={320} r="var(--radius-lg)" />
        ) : (
          <>
            <section className="rep-kpis" aria-label={t('learn.glance')}>
              <Kpi label={t('learn.k_todo')} value={String(data.counts.todo)} />
              <Kpi label={t('learn.k_late')} value={String(data.counts.late)} warn={data.counts.late > 0} />
              <Kpi label={t('learn.k_done')} value={String(data.counts.done)} />
            </section>
            {!data.courses.length ? (
              <section className="panel">
                <EmptyState icon="graduation" title={t('learn.none')} body={t('learn.none_body')} />
              </section>
            ) : (
              <div className="lr-cards">
                {data.courses.map((c) => (
                  <CourseCard key={c.id} c={c} onCertificate={() => setCert(c.enrolment)} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
      {cert ? <CertificateDialog enrolment={cert} onClose={() => setCert(null)} /> : null}
    </Shell>
  );
}

function CourseCard({ c, onCertificate }: { c: MyCourse; onCertificate: () => void }) {
  const value = c.state === 'done' ? 100 : pct(c.lessons_done, c.lessons);
  return (
    <article className="panel lr-card">
      <div className={`lr-band tone-${c.tone}`} aria-hidden="true">
        <Icon name="graduation" size={28} />
      </div>
      <div className="lr-card-body">
        <div className="lr-card-top">
          {c.category ? <span className="lr-cat">{c.category}</span> : <span />}
          {c.state === 'done' ? (
            <StatusBadge tone="ok" icon="checkCircle" label={t('learn.st_done')} />
          ) : c.late ? (
            <StatusBadge tone="danger" icon="alert" label={t('learn.late_since', { day: dayText(c.due!) })} />
          ) : c.due ? (
            <StatusBadge tone="warn" icon="calendar" label={t('learn.due_on', { day: dayText(c.due) })} />
          ) : c.state === 'new' ? (
            <StatusBadge tone="info" icon="sparkle" label={t('learn.st_new')} />
          ) : null}
        </div>
        <h2>
          <Link to={`/learning/${c.id}`}>{c.title}</Link>
        </h2>
        {c.summary ? <p className="muted small lr-summary">{c.summary}</p> : null}
        <p className="lr-meta muted small">
          <Icon name="list" size={14} /> {tn('learn.n_lessons', c.lessons)} · <Icon name="clock" size={14} /> {minutesText(c.minutes)}
        </p>
        <div className="lr-card-foot">
          <Progress value={value} label={t('learn.progress_of', { title: c.title })} />
          <span className="small">{value}%</span>
        </div>
        <div className="lr-card-actions">
          {c.state === 'done' ? (
            <>
              <Link className="btn btn-ghost btn-sm" to={`/learning/${c.id}`}>
                <span className="btn-label">{t('learn.review')}</span>
              </Link>
              {c.certificate_no !== null ? (
                <Button size="sm" icon="badgeCheck" onClick={onCertificate}>
                  {t('learn.certificate')}
                </Button>
              ) : null}
            </>
          ) : (
            <Link className="btn btn-primary btn-sm" to={`/learning/${c.id}`}>
              <Icon name="play" size={16} />
              <span className="btn-label">{c.state === 'started' ? t('learn.continue') : t('learn.start')}</span>
            </Link>
          )}
        </div>
      </div>
    </article>
  );
}

// ---- a course -----------------------------------------------------------------------------

export function LearningCourse() {
  const { course = '', lesson: lessonId } = useParams();
  const navigate = useNavigate();
  const { data: o, reload } = useAction<Outline>('elearning.outline', { id: course });
  const [cert, setCert] = useState(false);
  const next = o?.lessons.find((l) => !l.done) ?? o?.lessons[0];
  const current = lessonId ?? null;
  const done = o ? o.lessons.filter((l) => l.done).length : 0;
  const go = (id: string) => navigate(`/learning/${course}/${id}`);
  const after = async () => {
    await reload();
  };
  return (
    <Shell>
      <div className="page lr-course">
        <nav className="crumbs" aria-label={t('learn.crumbs')}>
          <Link to="/learning">{t('learn.title')}</Link>
          <Icon name="chevronRight" size={14} />
          <span>{o?.course.title ?? '…'}</span>
        </nav>
        {!o ? (
          <Skeleton h={360} r="var(--radius-lg)" />
        ) : (
          <div className="lr-layout">
            <aside className="panel lr-outline" aria-label={t('learn.lessons')}>
              <h2>{o.course.title}</h2>
              <div className="lr-card-foot">
                <Progress value={pct(done, o.lessons.length)} label={t('learn.progress_of', { title: o.course.title })} />
                <span className="small">
                  {done}/{o.lessons.length}
                </span>
              </div>
              <ol className="lr-lessons">
                {o.lessons.map((l, i) => (
                  <li key={l.id}>
                    <Link to={`/learning/${course}/${l.id}`} className={`lr-lesson-link${l.id === current ? ' on' : ''}${l.done ? ' done' : ''}`} aria-current={l.id === current ? 'page' : undefined}>
                      <span className="lr-step" aria-hidden="true">
                        {l.done ? <Icon name="check" size={14} /> : i + 1}
                      </span>
                      <span className="lr-lesson-name">
                        {l.title}
                        <span className="muted small">
                          <Icon name={KIND_ICON[l.kind]} size={12} /> {t(`learn.kind_${l.kind}` as MessageKey)} · {tn('learn.minutes', l.minutes)}
                          {l.kind === 'quiz' && l.best !== null ? ` · ${t('learn.best', { n: l.best })}` : ''}
                        </span>
                      </span>
                      {l.done ? <span className="sr-only">{t('learn.st_done')}</span> : null}
                    </Link>
                  </li>
                ))}
              </ol>
            </aside>
            <div className="lr-main">
              {o.enrolment?.state === 'done' ? (
                <section className="panel lr-finished" role="status">
                  <Icon name="badgeCheck" size={32} />
                  <div>
                    <h2>{t('learn.finished_title')}</h2>
                    <p className="muted">
                      {t('learn.finished_on', { day: dayText(o.enrolment.done_at!.slice(0, 10)) })}
                      {o.enrolment.score !== null ? ` · ${t('learn.score', { n: o.enrolment.score })}` : ''}
                    </p>
                  </div>
                  {o.enrolment.certificate_no !== null ? (
                    <Button variant="primary" icon="badgeCheck" onClick={() => setCert(true)}>
                      {t('learn.certificate')}
                    </Button>
                  ) : null}
                </section>
              ) : null}
              {current ? (
                <LessonPanel key={current} id={current} outline={o} onChanged={after} onNext={go} />
              ) : (
                <section className="panel lr-intro">
                  <span className={`lr-intro-icon tone-${o.course.tone}`} aria-hidden="true">
                    <Icon name="graduation" size={32} />
                  </span>
                  <h1>{o.course.title}</h1>
                  {o.course.summary ? <p className="lr-lead">{o.course.summary}</p> : null}
                  <ul className="lr-facts">
                    <li>
                      <Icon name="list" size={16} /> {tn('learn.n_lessons', o.lessons.length)}
                    </li>
                    <li>
                      <Icon name="clock" size={16} /> {minutesText(o.lessons.reduce((a, l) => a + l.minutes, 0))}
                    </li>
                    {o.lessons.some((l) => l.kind === 'quiz') ? (
                      <li>
                        <Icon name="checkSquare" size={16} /> {t('learn.pass_mark', { n: o.course.pass_mark })}
                      </li>
                    ) : null}
                    {o.course.certificate ? (
                      <li>
                        <Icon name="badgeCheck" size={16} /> {t('learn.with_certificate')}
                      </li>
                    ) : null}
                    {o.enrolment?.due ? (
                      <li>
                        <Icon name="calendar" size={16} /> {t('learn.due_on', { day: dayText(o.enrolment.due) })}
                      </li>
                    ) : null}
                  </ul>
                  {next ? (
                    <Button variant="primary" icon="play" onClick={() => go(next.id)}>
                      {o.enrolment?.state === 'done' ? t('learn.review') : done ? t('learn.continue') : t('learn.start')}
                    </Button>
                  ) : null}
                </section>
              )}
            </div>
          </div>
        )}
      </div>
      {cert && o?.enrolment ? <CertificateDialog enrolment={o.enrolment.id} onClose={() => setCert(false)} /> : null}
    </Shell>
  );
}

function LessonPanel({ id, outline, onChanged, onNext }: { id: string; outline: Outline; onChanged: () => Promise<void>; onNext: (id: string) => void }) {
  const { data: l, reload } = useAction<LessonView>('elearning.lesson', { id });
  const { busy, run } = useSave();
  const at = outline.lessons.findIndex((x) => x.id === id);
  const nextLesson = outline.lessons[at + 1];
  const markDone = () =>
    run(async () => {
      await action('elearning.complete', { id });
      await reload();
      await onChanged();
    }, t('learn.lesson_done'));
  if (!l) return <Skeleton h={300} r="var(--radius-lg)" />;
  return (
    <section className="panel lr-lesson" aria-labelledby="lr-lesson-title">
      <p className="lr-kicker muted small">
        <Icon name={KIND_ICON[l.kind]} size={14} /> {t('learn.lesson_n', { n: at + 1, total: outline.lessons.length })} · {tn('learn.minutes', l.minutes)}
      </p>
      <h1 id="lr-lesson-title">{l.title}</h1>
      {l.kind === 'quiz' ? (
        <Quiz l={l} onDone={async () => (await reload(), await onChanged())} />
      ) : (
        <>
          <Body text={l.body} />
          {l.url ? (
            <a className="lr-open" href={l.url} target="_blank" rel="noopener noreferrer">
              <Icon name={l.kind === 'video' ? 'play' : 'file'} size={24} />
              <span>
                <strong>{l.kind === 'video' ? t('learn.watch') : t('learn.open_doc')}</strong>
                <span className="muted small">{t('learn.opens_new')}</span>
              </span>
              <Icon name="external" size={16} />
            </a>
          ) : null}
        </>
      )}
      <div className="lr-lesson-foot">
        {l.kind !== 'quiz' ? (
          l.done ? (
            <StatusBadge tone="ok" icon="checkCircle" label={t('learn.st_done')} />
          ) : (
            <Button variant="primary" icon="check" busy={busy} onClick={() => void markDone()}>
              {l.kind === 'text' ? t('learn.mark_read') : l.kind === 'video' ? t('learn.mark_watched') : t('learn.mark_read_doc')}
            </Button>
          )
        ) : (
          <span />
        )}
        {nextLesson ? (
          <Button variant={l.done ? 'primary' : 'ghost'} onClick={() => onNext(nextLesson.id)}>
            {t('learn.next')}
            <Icon name="chevronRight" size={16} />
          </Button>
        ) : null}
      </div>
    </section>
  );
}

function Quiz({ l, onDone }: { l: LessonView; onDone: () => Promise<void> }) {
  const [answers, setAnswers] = useState<Record<string, number[]>>({});
  const [result, setResult] = useState<QuizResult | null>(null);
  const { busy, run } = useSave();
  const missing = l.questions.filter((q) => !(answers[q.id] ?? []).length).length;
  const pick = (q: LessonView['questions'][number], i: number, on: boolean) => {
    if (result) return;
    const cur = answers[q.id] ?? [];
    setAnswers({ ...answers, [q.id]: q.multiple ? (on ? [...cur, i] : cur.filter((x) => x !== i)) : [i] });
  };
  const byId = new Map(result?.questions.map((r) => [r.id, r]));
  return (
    <div className="lr-quiz">
      <p className="muted">
        {t('learn.quiz_lead', { n: l.pass_mark })}
        {l.attempts ? ` ${tn('learn.quiz_tried', l.attempts, { best: l.best ?? 0 })}` : ''}
      </p>
      {result ? (
        <div className={`lr-result ${result.passed ? 'pass' : 'fail'}`} role="status">
          <span className="lr-result-score">{result.score}%</span>
          <span>
            <strong>{result.passed ? t('learn.passed') : t('learn.not_passed', { n: result.pass_mark })}</strong>
            <span className="small">{result.course_done ? t('learn.course_done_now') : result.passed ? t('learn.passed_hint') : t('learn.try_again_hint')}</span>
          </span>
        </div>
      ) : null}
      <ol className="lr-questions">
        {l.questions.map((q, n) => {
          const r = byId.get(q.id);
          return (
            <li key={q.id} className={r ? (r.right ? 'right' : 'wrong') : undefined}>
              <fieldset>
                <legend>
                  <span className="lr-qn">{n + 1}</span>
                  <span>
                    {q.text}
                    {q.multiple ? <span className="muted small lr-multi">{t('learn.choose_all')}</span> : null}
                  </span>
                </legend>
                {q.options.map((o, i) => {
                  const chosen = (answers[q.id] ?? []).includes(i);
                  const correct = r?.correct.includes(i);
                  return (
                    <label key={i} className={`lr-option${chosen ? ' chosen' : ''}${r && correct ? ' correct' : ''}${r && chosen && !correct ? ' wrong' : ''}`}>
                      <input type={q.multiple ? 'checkbox' : 'radio'} name={`q-${q.id}`} checked={chosen} disabled={!!result} onChange={(e) => pick(q, i, e.target.checked)} />
                      <span>{o}</span>
                      {r && correct ? <Icon name="check" size={16} /> : r && chosen ? <Icon name="x" size={16} /> : null}
                    </label>
                  );
                })}
                {r && !r.right ? (
                  <p className="lr-explain small">
                    <Icon name="info" size={14} /> {r.explain ?? t('learn.see_right')}
                  </p>
                ) : null}
              </fieldset>
            </li>
          );
        })}
      </ol>
      <div className="lr-quiz-foot">
        {result ? (
          result.passed ? null : (
            <Button
              icon="repeat"
              onClick={() => {
                setResult(null);
                setAnswers({});
              }}
            >
              {t('learn.try_again')}
            </Button>
          )
        ) : (
          <>
            <span className="muted small">{missing ? tn('learn.left_to_answer', missing) : t('learn.all_answered')}</span>
            <Button
              variant="primary"
              icon="send"
              busy={busy}
              disabled={missing > 0}
              onClick={() =>
                void run(async () => {
                  const r = await action<QuizResult>('elearning.answer', { id: l.id, answers: Object.entries(answers).map(([question, choices]) => ({ question, choices })) });
                  setResult(r);
                  await onDone();
                })
              }
            >
              {t('learn.submit')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

function CertificateDialog({ enrolment, onClose }: { enrolment: string; onClose: () => void }) {
  const { data } = useAction<{ number: number; course: string; person: string; business: string; done_at: string; score: number | null; minutes: number }>('elearning.certificate', { enrolment });
  return (
    <Dialog
      title={t('learn.certificate')}
      size="lg"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.close')}
          </Button>
          <Button variant="primary" icon="printer" disabled={!data} onClick={() => window.print()}>
            {t('learn.print')}
          </Button>
        </>
      }
    >
      {!data ? (
        <Skeleton h={300} />
      ) : (
        <div className="lr-cert print-area">
          <Icon name="badgeCheck" size={40} />
          <p className="lr-cert-kicker">{t('learn.cert_kicker')}</p>
          <p className="lr-cert-person">{data.person}</p>
          <p>{t('learn.cert_for')}</p>
          <p className="lr-cert-course">{data.course}</p>
          <p className="muted">
            {t('learn.cert_on', { day: dayText(data.done_at.slice(0, 10)) })} · {minutesText(data.minutes)}
            {data.score !== null ? ` · ${t('learn.score', { n: data.score })}` : ''}
          </p>
          <div className="lr-cert-foot">
            <span>{data.business}</span>
            <span className="muted small">{t('learn.cert_no', { n: data.number })}</span>
          </div>
        </div>
      )}
    </Dialog>
  );
}

// ---- managers -----------------------------------------------------------------------------

interface CourseRow {
  id: string;
  title: string;
  category: string | null;
  tone: string;
  state: 'draft' | 'published' | 'archived';
  audience: 'everyone' | 'assigned';
  lessons: number;
  minutes: number;
  enrolled: number;
  done: number;
  late: number;
  avg_score: number | null;
}
const STATE_TONE = { draft: 'neutral', published: 'ok', archived: 'neutral' } as const;
const STATE_ICON: Record<'draft' | 'published' | 'archived', IconName> = { draft: 'pen', published: 'checkCircle', archived: 'archive' };

export function LearningManage() {
  const navigate = useNavigate();
  const { data } = useAction<CourseRow[]>('elearning.courses');
  return (
    <Shell>
      <div className="page reports lr-page">
        <nav className="crumbs" aria-label={t('learn.crumbs')}>
          <Link to="/learning">{t('learn.title')}</Link>
          <Icon name="chevronRight" size={14} />
          <span>{t('learn.manage')}</span>
        </nav>
        <div className="page-head">
          <div>
            <h1>{t('learn.manage')}</h1>
            <p className="muted">{t('learn.manage_lead')}</p>
          </div>
          <Button variant="primary" icon="plus" onClick={() => navigate('/learning/manage/new')}>
            {t('learn.new_course')}
          </Button>
        </div>
        {!data ? (
          <Skeleton h={300} r="var(--radius-lg)" />
        ) : !data.length ? (
          <section className="panel">
            <EmptyState icon="graduation" title={t('learn.no_courses')} body={t('learn.no_courses_body')} action={<Button icon="plus" onClick={() => navigate('/learning/manage/new')}>{t('learn.new_course')}</Button>} />
          </section>
        ) : (
          <section className="panel">
            <Table
              view="learning.courses"
              head={[t('learn.course'), t('learn.state'), t('learn.lessons'), t('learn.people'), t('learn.finished'), t('learn.late'), t('learn.avg_score')]}
              left={[1]}
              phoneHide={[2, 4, 6]}
              rows={data.map((c) => ({
                key: c.id,
                onClick: () => navigate(`/learning/manage/${c.id}`),
                cells: [
                  <span className="lr-row-title">
                    <span className={`lr-dot tone-${c.tone}`} aria-hidden="true" />
                    <span>
                      <Link to={`/learning/manage/${c.id}`}>{c.title}</Link>
                      <span className="muted small">
                        {c.category ? `${c.category} · ` : ''}
                        {t(`learn.aud_${c.audience}` as MessageKey)}
                      </span>
                    </span>
                  </span>,
                  <StatusBadge tone={STATE_TONE[c.state]} icon={STATE_ICON[c.state]} label={t(`learn.st_${c.state}` as MessageKey)} />,
                  `${c.lessons} · ${minutesText(c.minutes)}`,
                  String(c.enrolled),
                  c.enrolled ? `${c.done} (${pct(c.done, c.enrolled)}%)` : '—',
                  c.late ? <strong className="lr-late">{c.late}</strong> : '0',
                  c.avg_score === null ? '—' : `${c.avg_score}%`,
                ],
              }))}
            />
          </section>
        )}
      </div>
    </Shell>
  );
}

interface EditQuestion {
  id?: string | null;
  text: string;
  options: { text: string; correct: boolean }[];
  explain: string | null;
}
interface EditLesson {
  id?: string | null;
  key: string;
  title: string;
  kind: Kind;
  body: string | null;
  url: string | null;
  minutes: number;
  questions: EditQuestion[];
}
interface EditCourse {
  id?: string | null;
  title: string;
  summary: string | null;
  category: string | null;
  tone: string;
  audience: 'everyone' | 'assigned';
  pass_mark: number;
  certificate: boolean;
  state?: 'draft' | 'published' | 'archived';
  lessons: EditLesson[];
}

let keySeq = 0;
const newKey = () => `n${++keySeq}`;
const blankQuestion = (): EditQuestion => ({ text: '', options: [{ text: '', correct: true }, { text: '', correct: false }], explain: null });
const blankLesson = (kind: Kind): EditLesson => ({ key: newKey(), title: '', kind, body: null, url: null, minutes: kind === 'quiz' ? 3 : 5, questions: kind === 'quiz' ? [blankQuestion()] : [] });

export function LearningEditor() {
  const { id = 'new' } = useParams();
  const isNew = id === 'new';
  const navigate = useNavigate();
  const [tab, setTab] = useState<'content' | 'people'>('content');
  const [c, setC] = useState<EditCourse | null>(
    isNew ? { title: '', summary: null, category: null, tone: 'blue', audience: 'assigned', pass_mark: 80, certificate: true, lessons: [blankLesson('text')] } : null,
  );
  const [open, setOpen] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [drop, setDrop] = useState(false);
  const { busy, run } = useSave();
  const load = async () => {
    const r = await action<EditCourse & { lessons: (EditLesson & { id: string })[] }>('elearning.get_course', { id });
    setC({ ...r, lessons: r.lessons.map((l) => ({ ...l, key: String(l.id) })) });
    setDirty(false);
  };
  useEffect(() => {
    if (!isNew) void load();
  }, [id]);
  const set = (patch: Partial<EditCourse>) => {
    setC((x) => (x ? { ...x, ...patch } : x));
    setDirty(true);
  };
  const setLesson = (key: string, patch: Partial<EditLesson>) => set({ lessons: c!.lessons.map((l) => (l.key === key ? { ...l, ...patch } : l)) });
  const move = (i: number, by: number) => {
    const ls = [...c!.lessons];
    const [x] = ls.splice(i, 1);
    ls.splice(i + by, 0, x!);
    set({ lessons: ls });
  };
  const save = () =>
    run(async () => {
      const { state: _s, ...body } = c!;
      const r = await action<{ id: string }>('elearning.save_course', { ...body, lessons: c!.lessons.map(({ key: _k, ...l }) => l) });
      if (isNew) navigate(`/learning/manage/${r.id}`, { replace: true });
      else await load();
    }, t('learn.saved'));
  const setState = (state: 'draft' | 'published' | 'archived') =>
    run(async () => {
      if (dirty) await save();
      await action('elearning.set_state', { id, state });
      await load();
    }, t(`learn.now_${state}` as MessageKey));
  return (
    <Shell>
      <div className="page lr-editor">
        <nav className="crumbs" aria-label={t('learn.crumbs')}>
          <Link to="/learning/manage">{t('learn.manage')}</Link>
          <Icon name="chevronRight" size={14} />
          <span>{c?.title || t('learn.new_course')}</span>
        </nav>
        {!c ? (
          <Skeleton h={400} r="var(--radius-lg)" />
        ) : (
          <>
            <div className="page-head">
              <div>
                <h1>{c.title || t('learn.new_course')}</h1>
                <p className="muted">
                  {c.state ? <StatusBadge tone={STATE_TONE[c.state]} icon={STATE_ICON[c.state]} label={t(`learn.st_${c.state}` as MessageKey)} /> : null}{' '}
                  {tn('learn.n_lessons', c.lessons.length)} · {minutesText(c.lessons.reduce((a, l) => a + (l.minutes || 0), 0))}
                </p>
              </div>
              <span className="lr-head-actions">
                {!isNew && c.state !== 'published' ? (
                  <Button icon="send" busy={busy} onClick={() => void setState('published')}>
                    {t('learn.publish')}
                  </Button>
                ) : null}
                {c.state === 'published' ? (
                  <Button variant="ghost" icon="archive" busy={busy} onClick={() => void setState('archived')}>
                    {t('learn.archive')}
                  </Button>
                ) : null}
                <Button variant="primary" icon="check" busy={busy} disabled={!dirty && !isNew} onClick={() => void save()}>
                  {t('common.save')}
                </Button>
              </span>
            </div>
            {!isNew ? (
              <div className="segmented tabs" role="tablist" aria-label={t('learn.sections')}>
                {(['content', 'people'] as const).map((k) => (
                  <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? 'active' : ''} onClick={() => setTab(k)}>
                    <Icon name={k === 'content' ? 'book' : 'users'} size={16} />
                    {t(`learn.tab_${k}` as MessageKey)}
                  </button>
                ))}
              </div>
            ) : null}
            {tab === 'people' && !isNew ? (
              <PeopleTab course={id} archived={c.state === 'archived'} />
            ) : (
              <div className="lr-edit-grid">
                <section className="panel lr-settings">
                  <h2>{t('learn.about')}</h2>
                  <Field label={t('learn.course_title')} required>
                    <input className="input" value={c.title} maxLength={160} onChange={(e) => set({ title: e.target.value })} />
                  </Field>
                  <Field label={t('learn.summary')}>
                    <textarea className="input" rows={3} value={c.summary ?? ''} maxLength={2000} onChange={(e) => set({ summary: e.target.value || null })} />
                  </Field>
                  <Field label={t('learn.category')} hint={t('learn.category_hint')}>
                    <input className="input" value={c.category ?? ''} maxLength={60} onChange={(e) => set({ category: e.target.value || null })} />
                  </Field>
                  <Field label={t('learn.colour')}>
                    <span className="tone-picker" role="radiogroup" aria-label={t('learn.colour')}>
                      {TONES.map((x) => (
                        <button key={x} type="button" role="radio" aria-checked={c.tone === x} aria-label={t(`todo.tone_${x}` as MessageKey)} className={`tone-dot tone-${x}${c.tone === x ? ' active' : ''}`} onClick={() => set({ tone: x })} />
                      ))}
                    </span>
                  </Field>
                  <Field label={t('learn.who')}>
                    <select className="input" value={c.audience} onChange={(e) => set({ audience: e.target.value as EditCourse['audience'] })}>
                      <option value="assigned">{t('learn.aud_assigned')}</option>
                      <option value="everyone">{t('learn.aud_everyone')}</option>
                    </select>
                  </Field>
                  <Field label={t('learn.pass_mark_label')} hint={t('learn.pass_mark_hint')}>
                    <input className="input" type="number" min={0} max={100} value={c.pass_mark} onChange={(e) => set({ pass_mark: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })} />
                  </Field>
                  <label className="switch-row">
                    <span>{t('learn.give_certificate')}</span>
                    <Switch checked={c.certificate} onChange={(v) => set({ certificate: v })} label={t('learn.give_certificate')} />
                  </label>
                  {!isNew && c.state === 'draft' ? (
                    <Button variant="ghost" icon="trash" onClick={() => setDrop(true)}>
                      {t('learn.delete_course')}
                    </Button>
                  ) : null}
                </section>
                <section className="lr-lessons-edit" aria-label={t('learn.lessons')}>
                  {c.lessons.map((l, i) => (
                    <LessonEditor
                      key={l.key}
                      l={l}
                      n={i + 1}
                      open={open === l.key || (!l.title && open === null)}
                      onToggle={() => setOpen(open === l.key ? '' : l.key)}
                      onChange={(p) => setLesson(l.key, p)}
                      onUp={i > 0 ? () => move(i, -1) : undefined}
                      onDown={i < c.lessons.length - 1 ? () => move(i, 1) : undefined}
                      onRemove={() => set({ lessons: c.lessons.filter((x) => x.key !== l.key) })}
                    />
                  ))}
                  <div className="lr-add-lesson">
                    <span className="muted small">{t('learn.add_lesson')}</span>
                    {KINDS.map((k) => (
                      <Button
                        key={k}
                        size="sm"
                        icon={KIND_ICON[k]}
                        onClick={() => {
                          const l = blankLesson(k);
                          set({ lessons: [...c.lessons, l] });
                          setOpen(l.key);
                        }}
                      >
                        {t(`learn.kind_${k}` as MessageKey)}
                      </Button>
                    ))}
                  </div>
                </section>
              </div>
            )}
          </>
        )}
      </div>
      {drop ? (
        <Confirm
          title={t('learn.delete_title', { name: c?.title ?? '' })}
          body={t('learn.delete_body')}
          confirm={t('learn.delete_course')}
          danger
          busy={busy}
          onClose={() => setDrop(false)}
          onConfirm={() =>
            void run(async () => {
              await action('elearning.delete_course', { id });
              navigate('/learning/manage', { replace: true });
            }, t('learn.deleted'))
          }
        />
      ) : null}
    </Shell>
  );
}

function LessonEditor({
  l,
  n,
  open,
  onToggle,
  onChange,
  onUp,
  onDown,
  onRemove,
}: {
  l: EditLesson;
  n: number;
  open: boolean;
  onToggle: () => void;
  onChange: (p: Partial<EditLesson>) => void;
  onUp?: () => void;
  onDown?: () => void;
  onRemove: () => void;
}) {
  const setQ = (i: number, p: Partial<EditQuestion>) => onChange({ questions: l.questions.map((q, j) => (j === i ? { ...q, ...p } : q)) });
  return (
    <article className={`panel lr-ledit${open ? ' open' : ''}`}>
      <div className="lr-ledit-head">
        <button type="button" className="lr-ledit-toggle" aria-expanded={open} onClick={onToggle}>
          <span className="lr-step">{n}</span>
          <Icon name={KIND_ICON[l.kind]} size={16} />
          <span className="lr-ledit-title">{l.title || t('learn.untitled')}</span>
          <span className="muted small">
            {t(`learn.kind_${l.kind}` as MessageKey)}
            {l.kind === 'quiz' ? ` · ${tn('learn.n_questions', l.questions.length)}` : ''}
          </span>
          <Icon name={open ? 'up' : 'down'} size={16} />
        </button>
        <span className="lr-ledit-tools">
          <IconButton icon="arrowUp" label={t('learn.move_up')} disabled={!onUp} onClick={onUp} />
          <IconButton icon="arrowDown" label={t('learn.move_down')} disabled={!onDown} onClick={onDown} />
          <IconButton icon="trash" label={t('learn.remove_lesson')} onClick={onRemove} />
        </span>
      </div>
      {open ? (
        <div className="lr-ledit-body">
          <div className="lr-ledit-row">
            <Field label={t('learn.lesson_title')} required>
              <input className="input" value={l.title} maxLength={160} onChange={(e) => onChange({ title: e.target.value })} />
            </Field>
            <Field label={t('learn.kind')}>
              <select className="input" value={l.kind} onChange={(e) => onChange({ kind: e.target.value as Kind, questions: e.target.value === 'quiz' && !l.questions.length ? [blankQuestion()] : l.questions })}>
                {KINDS.map((k) => (
                  <option key={k} value={k}>
                    {t(`learn.kind_${k}` as MessageKey)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t('learn.minutes_label')}>
              <input className="input" type="number" min={1} max={600} value={l.minutes} onChange={(e) => onChange({ minutes: Math.max(1, Number(e.target.value) || 1) })} />
            </Field>
          </div>
          {l.kind === 'video' || l.kind === 'document' ? (
            <Field label={l.kind === 'video' ? t('learn.video_link') : t('learn.doc_link')} hint={t('learn.link_hint')} required>
              <input className="input" type="url" inputMode="url" placeholder="https://" value={l.url ?? ''} onChange={(e) => onChange({ url: e.target.value || null })} />
            </Field>
          ) : null}
          {l.kind !== 'quiz' ? (
            <Field label={l.kind === 'text' ? t('learn.text') : t('learn.notes')} hint={t('learn.text_hint')}>
              <textarea className="input" rows={l.kind === 'text' ? 8 : 3} value={l.body ?? ''} onChange={(e) => onChange({ body: e.target.value || null })} />
            </Field>
          ) : (
            <>
              <ol className="lr-qedit">
                {l.questions.map((q, i) => (
                  <li key={i}>
                    <div className="lr-qedit-head">
                      <Field label={t('learn.question_n', { n: i + 1 })} required>
                        <textarea className="input" rows={2} value={q.text} onChange={(e) => setQ(i, { text: e.target.value })} />
                      </Field>
                      <IconButton icon="trash" label={t('learn.remove_question')} disabled={l.questions.length === 1} onClick={() => onChange({ questions: l.questions.filter((_, j) => j !== i) })} />
                    </div>
                    <p className="muted small">{t('learn.tick_right')}</p>
                    <ul className="lr-oedit">
                      {q.options.map((o, j) => (
                        <li key={j}>
                          <input
                            type="checkbox"
                            checked={o.correct}
                            aria-label={t('learn.right_answer_n', { n: j + 1 })}
                            onChange={(e) => setQ(i, { options: q.options.map((x, k) => (k === j ? { ...x, correct: e.target.checked } : x)) })}
                          />
                          <input
                            className="input"
                            value={o.text}
                            maxLength={300}
                            aria-label={t('learn.option_n', { n: j + 1 })}
                            placeholder={t('learn.option_n', { n: j + 1 })}
                            onChange={(e) => setQ(i, { options: q.options.map((x, k) => (k === j ? { ...x, text: e.target.value } : x)) })}
                          />
                          <IconButton icon="x" label={t('learn.remove_option')} disabled={q.options.length <= 2} onClick={() => setQ(i, { options: q.options.filter((_, k) => k !== j) })} />
                        </li>
                      ))}
                    </ul>
                    {q.options.length < 10 ? (
                      <Button size="sm" variant="ghost" icon="plus" onClick={() => setQ(i, { options: [...q.options, { text: '', correct: false }] })}>
                        {t('learn.add_option')}
                      </Button>
                    ) : null}
                    <Field label={t('learn.explain')} hint={t('learn.explain_hint')}>
                      <input className="input" value={q.explain ?? ''} maxLength={1000} onChange={(e) => setQ(i, { explain: e.target.value || null })} />
                    </Field>
                  </li>
                ))}
              </ol>
              <Button size="sm" icon="plus" onClick={() => onChange({ questions: [...l.questions, blankQuestion()] })}>
                {t('learn.add_question')}
              </Button>
            </>
          )}
        </div>
      ) : null}
    </article>
  );
}

interface Report {
  course: { id: string; title: string; state: string; pass_mark: number };
  lessons: number;
  people: { id: string; user: string; name: string; state: string; due: string | null; done_at: string | null; score: number | null; lessons_done: number; attempts: number; late: boolean }[];
  quizzes: { lesson: string; takers: number; avg_best: number | null; avg_attempts: number | null }[];
}

function PeopleTab({ course, archived }: { course: string; archived: boolean }) {
  const { data, reload } = useAction<Report>('elearning.report', { course });
  const [assign, setAssign] = useState(false);
  const [drop, setDrop] = useState<Report['people'][number] | null>(null);
  const { busy, run } = useSave();
  if (!data) return <Skeleton h={300} r="var(--radius-lg)" />;
  const done = data.people.filter((p) => p.state === 'done');
  const scores = done.filter((p) => p.score !== null).map((p) => p.score!);
  return (
    <>
      <section className="rep-kpis" aria-label={t('learn.glance')}>
        <Kpi label={t('learn.people')} value={String(data.people.length)} />
        <Kpi label={t('learn.finished')} value={data.people.length ? `${pct(done.length, data.people.length)}%` : '—'} />
        <Kpi label={t('learn.late')} value={String(data.people.filter((p) => p.late).length)} warn={data.people.some((p) => p.late)} />
        <Kpi label={t('learn.avg_score')} value={scores.length ? `${Math.round(scores.reduce((a, b) => a + b, 0) / scores.length)}%` : '—'} />
      </section>
      <section className="panel">
        <div className="panel-head lr-panel-head">
          <h2>{t('learn.who_takes')}</h2>
          {!archived ? (
            <Button variant="primary" icon="plus" onClick={() => setAssign(true)}>
              {t('learn.assign')}
            </Button>
          ) : null}
        </div>
        <Table
          head={[t('learn.person'), t('learn.progress'), t('learn.due'), t('learn.result'), '']}
          left={[1, 2, 3]}
          phoneHide={[2]}
          empty={t('learn.nobody')}
          rows={data.people.map((p) => ({
            key: p.id,
            cells: [
              <strong>{p.name}</strong>,
              <span className="lr-cell-progress">
                <Progress value={p.state === 'done' ? 100 : pct(p.lessons_done, data.lessons)} label={t('learn.progress_of', { title: p.name })} />
                <span className="small">
                  {p.state === 'done' ? t('learn.st_done') : p.state === 'assigned' ? t('learn.not_started') : `${p.lessons_done}/${data.lessons}`}
                </span>
              </span>,
              p.due ? <span className={p.late ? 'lr-late' : undefined}>{dayText(p.due)}</span> : '—',
              p.state === 'done' ? `${p.score === null ? t('learn.st_done') : `${p.score}%`} · ${dayText(p.done_at!.slice(0, 10))}` : p.attempts ? tn('learn.attempts', p.attempts) : '—',
              p.state !== 'done' ? <IconButton icon="x" label={t('learn.unassign_n', { name: p.name })} onClick={() => setDrop(p)} /> : null,
            ],
          }))}
        />
      </section>
      {data.quizzes.length ? (
        <section className="panel">
          <h2>{t('learn.quizzes')}</h2>
          <p className="muted small">{t('learn.quizzes_lead')}</p>
          <Table
            head={[t('learn.quiz'), t('learn.takers'), t('learn.avg_best'), t('learn.avg_attempts')]}
            rows={data.quizzes.map((q) => ({
              key: q.lesson,
              cells: [q.lesson, String(q.takers), q.avg_best === null ? '—' : `${q.avg_best}%`, q.avg_attempts === null ? '—' : String(q.avg_attempts)],
            }))}
          />
        </section>
      ) : null}
      {assign ? <AssignDialog course={course} already={new Set(data.people.map((p) => p.user))} onClose={() => setAssign(false)} onDone={reload} /> : null}
      {drop ? (
        <Confirm
          title={t('learn.unassign_title', { name: drop.name })}
          body={t('learn.unassign_body')}
          confirm={t('learn.unassign')}
          danger
          busy={busy}
          onClose={() => setDrop(null)}
          onConfirm={() =>
            void run(async () => {
              await action('elearning.unassign', { enrolment: drop.id });
              setDrop(null);
              await reload();
            }, t('learn.unassigned'))
          }
        />
      ) : null}
    </>
  );
}

function AssignDialog({ course, already, onClose, onDone }: { course: string; already: Set<string>; onClose: () => void; onDone: () => Promise<unknown> }) {
  const { data } = useAction<{ id: string; name: string; roles: string[] }[]>('elearning.people');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [due, setDue] = useState('');
  const [q, setQ] = useState('');
  const { busy, run } = useSave();
  const roles = useMemo(() => [...new Set((data ?? []).flatMap((p) => p.roles))].sort(), [data]);
  const shown = (data ?? []).filter((p) => !q || p.name.toLowerCase().includes(q.toLowerCase()));
  const toggle = (id: string, on: boolean) => {
    const next = new Set(picked);
    if (on) next.add(id);
    else next.delete(id);
    setPicked(next);
  };
  return (
    <Dialog
      title={t('learn.assign')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            icon="send"
            busy={busy}
            disabled={!picked.size}
            onClick={() =>
              void run(async () => {
                const r = await action<{ added: number; updated: number }>('elearning.assign', { course, users: [...picked], due: due || null });
                await onDone();
                onClose();
                return r;
              }, t('learn.assigned'))
            }
          >
            {tn('learn.assign_n', picked.size)}
          </Button>
        </>
      }
    >
      <Field label={t('learn.finish_by')} hint={t('learn.finish_by_hint')}>
        <input className="input" type="date" min={todayIso()} value={due} onChange={(e) => setDue(e.target.value)} />
      </Field>
      {roles.length > 1 ? (
        <div className="lr-roles" role="group" aria-label={t('learn.by_role')}>
          <span className="muted small">{t('learn.by_role')}</span>
          {roles.map((r) => (
            <button
              key={r}
              type="button"
              className="chip"
              onClick={() => setPicked(new Set([...picked, ...(data ?? []).filter((p) => p.roles.includes(r)).map((p) => p.id)]))}
            >
              <Icon name="plus" size={12} /> {t(`role.${r}` as MessageKey)}
            </button>
          ))}
        </div>
      ) : null}
      <input className="input" type="search" placeholder={t('learn.search_people')} aria-label={t('learn.search_people')} value={q} onChange={(e) => setQ(e.target.value)} />
      {!data ? (
        <Skeleton h={160} />
      ) : (
        <ul className="lr-pick">
          {shown.map((p) => (
            <li key={p.id}>
              <label>
                <input type="checkbox" checked={picked.has(p.id)} onChange={(e) => toggle(p.id, e.target.checked)} />
                <span className="lr-pick-name">{p.name}</span>
                <span className="muted small">{already.has(p.id) ? t('learn.already') : p.roles.map((r) => t(`role.${r}` as MessageKey)).join(', ')}</span>
              </label>
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
}

